import React, { ReactNode } from 'react';
import {
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormHelperText,
} from '@mui/material';
import { FieldValues } from 'react-hook-form';
import useFormInput, { FormInputBaseProps } from '../../hooks/useFormInput';

export interface CheckboxInputProps<T>
  extends Omit<FormInputBaseProps<T>, 'min' | 'max' | 'minLength' | 'maxLength'>,
    Pick<CheckboxProps, 'indeterminate'>,
    Pick<FormControlLabelProps, 'labelPlacement'> {
  /**
   * Enable this to make the checkbox appear checked when the value is false.
   * Useful to make labels more user-friendly without changing the data.
   */
  invert?: boolean;
  helperText?: ReactNode;
}

export default function CheckboxInput<T extends FieldValues>({
  indeterminate,
  labelPlacement,
  invert = false,
  helperText,
  ...baseProps
}: CheckboxInputProps<T>) {
  const { field, disabled, label, color, fullWidth, required, isError, errorMessage } = useFormInput<T>(baseProps);
  const { value, onChange, onBlur, name, ref } = field;

  return (
    <FormControl color={color} fullWidth={fullWidth} required={required} disabled={disabled} error={isError}>
      <FormControlLabel
        control={
          <Checkbox
            ref={ref}
            checked={invert ? !value : !!value}
            onChange={(e, checked) => onChange(invert ? !checked : checked)}
            onBlur={onBlur}
            name={name}
            color={color}
            indeterminate={indeterminate}
          />
        }
        label={required ? `${label} *` : label}
        labelPlacement={labelPlacement}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {!!errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
}
